/**
 * Printable Strategy Canvas — same paper / Paged.js treatment as item print.
 */

import { useCallback, useEffect, useMemo, useState, type ChangeEvent } from 'react';
import { useParams } from 'react-router-dom';
import { Loader2 } from 'lucide-react';

import { useStrategyCanvasStore, type StrategyCanvas } from '@/stores/prioritization/strategyCanvasStore';
import { useStrategyStore } from '@/stores/prioritization/strategyStore';
import { useTenantStore } from '@/stores/prioritization/tenantStore';
import { useOutcomeDriverStore } from '@/stores/prioritization/outcomeDriverStore';
import type { Strategy } from '@/types/prioritization/strategy';
import type { Goal, StrategicObjective } from '@/lib/strategyAlignment';
import { listGoals, listObjectives } from '@/lib/strategyAlignment';
import type { PrintPaperId } from '@/lib/print/paperPresets';
import {
  PRINT_PAPER_PRESETS,
  getPrintPaperPreset,
  loadSavedPrintPaperId,
  savePrintPaperId,
} from '@/lib/print/paperPresets';
import { PrintChrome } from '@/components/print/PrintChrome';
import { StrategyCanvasPrintBody } from '@/components/print/StrategyCanvasPrintBody';

/** Same visibility rule as StrategyCanvasEditor TacticsSubCell. */
function tacticItemBelongsOnCanvas(
  item: Goal | StrategicObjective,
  linkedStrategyIds: string[],
): boolean {
  const sid = (item as Goal).strategyId;
  if (!linkedStrategyIds.length) {
    return sid == null || sid === '';
  }
  return sid == null || sid === '' || linkedStrategyIds.includes(sid);
}

export function StrategyCanvasPrintPage() {
  const { canvasId } = useParams<{ canvasId: string }>();
  const workspaceId = useTenantStore((s) => s.currentWorkspaceId);

  const canvases = useStrategyCanvasStore((s) => s.canvases);
  const isLoadingCanvases = useStrategyCanvasStore((s) => s.isLoading);
  const fetchCanvases = useStrategyCanvasStore((s) => s.fetchCanvases);

  const strategiesMap = useStrategyStore((s) => s.strategies);
  const fetchStrategies = useStrategyStore((s) => s.fetchFromSupabase);

  const fetchOutcomeDrivers = useOutcomeDriverStore((s) => s.fetchFromSupabase);
  const outcomeDriversAll = useOutcomeDriverStore((s) => s.drivers);

  const [paperId, setPaperId] = useState<PrintPaperId>(() => loadSavedPrintPaperId());
  const [goalsRaw, setGoalsRaw] = useState<Goal[]>([]);
  const [objectivesRaw, setObjectivesRaw] = useState<StrategicObjective[]>([]);

  useEffect(() => {
    savePrintPaperId(paperId);
  }, [paperId]);

  useEffect(() => {
    if (!workspaceId) return;
    void fetchCanvases();
    void fetchStrategies();
    void fetchOutcomeDrivers();
  }, [workspaceId, fetchCanvases, fetchStrategies, fetchOutcomeDrivers]);

  useEffect(() => {
    if (!workspaceId) {
      setGoalsRaw([]);
      setObjectivesRaw([]);
      return;
    }
    void Promise.all([listGoals(workspaceId), listObjectives(workspaceId)]).then(([g, o]) => {
      setGoalsRaw(g);
      setObjectivesRaw(o);
    });
  }, [workspaceId]);

  const canvas: StrategyCanvas | undefined = canvasId
    ? canvases.find((c) => c.id === canvasId)
    : undefined;

  const allStrategies = useMemo(() => {
    const all = strategiesMap instanceof Map
      ? Array.from(strategiesMap.values())
      : Object.values(strategiesMap ?? {});
    return all.filter((s) => s.status !== 'archived') as Strategy[];
  }, [strategiesMap]);

  const linkedStrategies = useMemo(() => {
    if (!canvas) return [] as Strategy[];
    return allStrategies.filter((s) => canvas.linkedStrategyIds.includes(s.id));
  }, [canvas, allStrategies]);

  const linkedIds = canvas?.linkedStrategyIds ?? [];

  const objectivesOnCanvas = useMemo(
    () => objectivesRaw.filter((o) => tacticItemBelongsOnCanvas(o, linkedIds)),
    [objectivesRaw, linkedIds],
  );

  const goalsOnCanvas = useMemo(
    () => goalsRaw.filter((g) => tacticItemBelongsOnCanvas(g, linkedIds)),
    [goalsRaw, linkedIds],
  );

  /** Match Strategy Canvas UI: workspace drivers list (same as editor panels). */
  const driversForPrint = outcomeDriversAll;

  const preset = useMemo(() => getPrintPaperPreset(paperId), [paperId]);

  const docTitle = canvas?.title?.trim()
    ? `${canvas.title.trim()} — Strategy canvas print`
    : canvasId
      ? `Canvas ${canvasId.slice(0, 8)}… — Print`
      : 'Print';

  const pagedRenderKey = useMemo(() => {
    if (!canvas) return '';
    return [
      canvas.id,
      canvas.updatedAt,
      paperId,
      objectivesOnCanvas.length,
      goalsOnCanvas.length,
      linkedStrategies.length,
      driversForPrint.length,
      goalsRaw.length,
      objectivesRaw.length,
    ].join('|');
  }, [
    canvas,
    paperId,
    objectivesOnCanvas.length,
    goalsOnCanvas.length,
    linkedStrategies.length,
    driversForPrint.length,
    goalsRaw.length,
    objectivesRaw.length,
  ]);

  const handlePaperChange = useCallback((e: ChangeEvent<HTMLSelectElement>) => {
    setPaperId(e.target.value as PrintPaperId);
  }, []);

  if (!canvasId) {
    return (
      <div className="p-8 text-center text-sm text-[var(--text-sec)]">
        <p>No canvas selected.</p>
      </div>
    );
  }

  if (!workspaceId) {
    return (
      <div className="p-8 text-center">
        <p className="text-sm text-[var(--text-sec)]">Select a workspace to print this canvas.</p>
      </div>
    );
  }

  if (!canvas && isLoadingCanvases) {
    return (
      <div className="flex min-h-[40vh] items-center justify-center gap-2">
        <Loader2 className="h-6 w-6 animate-spin text-[var(--text-ter)]" />
        <span className="text-[var(--text-ter)]">Loading canvas…</span>
      </div>
    );
  }

  if (!canvas) {
    return (
      <div className="p-8 text-center">
        <p className="text-sm text-[var(--text-sec)]">Canvas not found.</p>
      </div>
    );
  }

  return (
    <PrintChrome
      documentTitle={docTitle}
      pageSizeCss={preset.pageSizeCss}
      previewMaxWidthMm={preset.previewMaxWidthMm}
      backHref={`/prioritization/strategy/canvas/${canvasId}`}
      backLabel="Return to canvas"
      pagedRenderKey={pagedRenderKey}
      toolbarAside={
        <label className="flex flex-wrap items-center gap-2 text-xs text-[var(--text-sec)] print:hidden">
          <span>Paper size</span>
          <select
            value={paperId}
            onChange={handlePaperChange}
            className="border border-[var(--rule)] bg-[var(--bg-raised)] px-2 py-1.5 text-xs text-[var(--text-pri)] shadow-sm"
          >
            {PRINT_PAPER_PRESETS.map((p) => (
              <option key={p.id} value={p.id}>
                {p.label}
              </option>
            ))}
          </select>
        </label>
      }
    >
      <StrategyCanvasPrintBody
        canvas={canvas}
        linkedStrategies={linkedStrategies}
        objectives={objectivesOnCanvas}
        goals={goalsOnCanvas}
        outcomeDrivers={driversForPrint}
      />
    </PrintChrome>
  );
}
